import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { paths } from "../../paths";
import logo from "../../assets/logo.png";

const NAV_ITEMS = [
  { label: "Dashboard", to: paths.dashboard },
  { label: "Leaderboard", to: paths.leaderboard },
  { label: "Tasks", to: paths.task },
  { label: "Rules", to: paths.rule },
  { label: "Contact", to: paths.contact },
  { label: "FAQ", to: paths.faq },
];

const ADMIN_NAV_ITEMS = [
  { label: "Dashboard", to: paths.adminDashboard },
  { label: "Manage CAs", to: paths.adminCas },
  { label: "Tasks", to: paths.adminTasks },
  { label: "Submissions", to: paths.adminSubmissions },
  { label: "Leaderboard", to: paths.adminLeaderboard },
  { label: "Announcements", to: paths.adminAnnouncements },
];

const MenuIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const Header = ({
  activeTab = "",
  showNav = true,
  admin = false,
  adminTab = "",
}) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const navItems = admin ? ADMIN_NAV_ITEMS : NAV_ITEMS;
  const currentTab = admin ? adminTab : activeTab;
  const renderNav = admin || showNav;

  const isItemActive = (item) =>
    admin ? item.to === currentTab : item.label === currentTab;

  const logout = () => {
    if (admin) {
      localStorage.removeItem("adminToken");
      navigate(paths.home);
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("userRole");
      sessionStorage.removeItem("dashboardCache");
      navigate(paths.home);
    }
  };

  return (
    <header className="relative z-50 flex items-center justify-between px-6 md:px-12 py-5 w-full box-border font-montserrat">
      {/* Logo — always returns to the main landing page */}
      <Link
        to={paths.home}
        className="flex-shrink-0"
      >
        <img
          src={logo}
          alt="Spirit"
          className="h-7 w-auto block cursor-pointer"
        />
      </Link>

      {/* Right side wrapper containing desktop navigation, logout button, avatar, and mobile toggle */}
      <div className="flex items-center gap-6">
        {/* Desktop navigation — aligned to the right side */}
        {renderNav && (
          <nav className="hidden lg:flex items-center gap-7">
            {navItems.map((item) => {
              const isActive = isItemActive(item);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`grid whitespace-nowrap text-[15px] transition-colors ${
                    isActive ? "text-white" : "text-white/80 hover:text-white"
                  }`}
                >
                  {/* Invisible bold copy reserves the widest width so the active
                      tab going bold never shifts the navbar between pages. */}
                  <span aria-hidden="true" className="col-start-1 row-start-1 font-semibold invisible">
                    {item.label}
                  </span>
                  <span className={`col-start-1 row-start-1 ${isActive ? "font-semibold" : "font-medium"}`}>
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </nav>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={logout}
            className="hidden sm:inline-flex text-[13px] font-semibold text-white/80 hover:text-white border border-white/25 hover:border-white/50 rounded-full px-4 py-1.5 transition-colors"
          >
            Log out
          </button>

          {/* Hamburger — mobile/tablet only */}
          {renderNav && (
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-white/30 text-white hover:border-white/60 transition-colors"
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu panel */}
      {renderNav && menuOpen && (
        <>
          {/* Tap-away backdrop */}
          <div
            className="lg:hidden fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
          />
          <nav className="lg:hidden absolute top-full left-4 right-4 mt-1 z-50 bg-[#2B1B17] rounded-2xl shadow-2xl border border-white/10 overflow-hidden flex flex-col py-2">
            {navItems.map((item) => {
              const isActive = isItemActive(item);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className={`px-5 py-3 text-[15px] transition-colors ${
                    isActive
                      ? "text-white font-semibold bg-white/10"
                      : "text-white/80 hover:text-white hover:bg-white/5 font-medium"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
              className="mt-1 mx-3 mb-1 text-[14px] font-semibold text-white/90 border border-white/25 hover:border-white/50 rounded-full px-4 py-2.5 transition-colors"
            >
              Log out
            </button>
          </nav>
        </>
      )}
    </header>
  );
};

export default Header;